import { cn } from 'cn';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  statusRegistro,
  type HistoricoAcompanhamentoOutputType,
  type PeriodoHistorico,
  type RegistroDoDiaOutputType,
} from '@shared/dto';

import { StatusRegistroBadge } from './registro-form/status-badge';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useApiLazyQuery, useApiQuery } from '../../hooks/use-api';
import { registrosApi } from '../../api/registros.api';
import { linhasCompletas } from '../../lib/registro-emojis';

const PERIODOS: { valor: PeriodoHistorico; label: string }[] = [
  { valor: 'DIA', label: 'Dia' },
  { valor: 'SEMANA', label: 'Semana' },
  { valor: 'MES', label: 'Mês' },
  { valor: 'TUDO', label: 'Tudo' },
];

function Metrica({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground uppercase">{titulo}</p>
      <p className="text-lg font-semibold">{valor}</p>
    </div>
  );
}

function formatarNota(valor: number | null): string {
  return valor === null ? '—' : valor.toFixed(1);
}

function formatarEvolucao(valor: number | null): string {
  if (valor === null) {
    return '—';
  }
  if (valor === 0) {
    return '±0.0';
  }
  return valor > 0 ? `+${valor.toFixed(1)}` : valor.toFixed(1);
}

/** "Segunda-feira, 07/09" -- hoje de verdade, a aba "Dia" nunca mostra outro dia. */
function formatarDiaEData(): string {
  const texto = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** `Date` no tipo, mas `fetch`+`JSON.parse` nunca revive datas -- valor real em runtime é string ISO. */
function paraData(valor: Date | string): Date {
  return valor instanceof Date ? valor : new Date(valor);
}

/** Como `formatarDiaEData`, pra data arbitrária da API; UTC de propósito (`RegistroAula.data` é `@db.Date`, senão o fuso local pode mostrar o dia errado). */
function formatarDiaEDataDe(valor: Date | string): string {
  const texto = paraData(valor).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function chaveDia(valor: Date | string): string {
  return paraData(valor).toISOString().slice(0, 10);
}

/** Janela de páginas do seletor: sempre 1ª, última, e um raio em torno da atual -- "..." nos buracos. */
function paginasVisiveis(atual: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const paginas = new Set<number>([1, total, atual - 1, atual, atual + 1]);
  const ordenadas = [...paginas].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const resultado: (number | 'ellipsis')[] = [];
  ordenadas.forEach((pagina, i) => {
    if (i > 0 && pagina - ordenadas[i - 1]! > 1) {
      resultado.push('ellipsis');
    }
    resultado.push(pagina);
  });
  return resultado;
}

/** Agrupa uma lista já ordenada em blocos consecutivos do mesmo dia. */
function agruparPorDia<T extends { data: Date | string }>(
  itens: T[],
): { chave: string; data: Date | string; itens: T[] }[] {
  const grupos: { chave: string; data: Date | string; itens: T[] }[] = [];
  for (const item of itens) {
    const chave = chaveDia(item.data);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.chave === chave) {
      ultimo.itens.push(item);
    } else {
      grupos.push({ chave, data: item.data, itens: [item] });
    }
  }
  return grupos;
}

/** Condensado por padrão (cabeçalho + pills por campo preenchido, `linhasCompletas`); clicar expande pro grid completo. */
function RegistroDoDiaCard({ registro }: { registro: RegistroDoDiaOutputType }) {
  const [expandido, setExpandido] = useState(false);
  const linhas = linhasCompletas(registro);

  return (
    <button
      type="button"
      onClick={() => setExpandido((v) => !v)}
      className="w-full rounded-lg border p-3 text-left transition-colors hover:bg-muted/30"
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{registro.horarioPrevisto}</span>
        <StatusRegistroBadge status={statusRegistro(registro)} />
      </div>
      <div className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground">
        <span>{registro.materiaNome}</span>
        <Badge
          className="border-transparent text-white"
          style={{ backgroundColor: registro.professorCor }}
        >
          {registro.professorNome}
        </Badge>
      </div>

      {linhas.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ainda não registrado.</p>
      ) : expandido ? (
        <>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {linhas.map((linha) => (
              <div key={linha.campoLabel}>
                <p className="text-xs text-muted-foreground">
                  {linha.campoLabel} {linha.topicoEmoji}
                </p>
                <p className="flex items-center gap-1.5 text-sm">
                  <span>{linha.valorEmoji}</span>
                  <span>{linha.label}</span>
                </p>
              </div>
            ))}
          </div>

          {registro.conteudos.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t pt-2">
              {registro.conteudos.map((conteudo) => (
                <span
                  key={conteudo.id}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                >
                  {conteudo.nome}
                </span>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {linhas.map((linha) => (
            <span
              key={linha.campoLabel}
              className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600"
            >
              {linha.topicoEmoji} {linha.valorEmoji} {linha.label}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

/** `registrosDoDia` vazio = sem horário previsto hoje; item sem `chegada` = previsto mas não registrado ("Ainda não registrado." dentro do card). */
function AbaDia({ registrosDoDia }: { registrosDoDia: RegistroDoDiaOutputType[] | null }) {
  const registros = registrosDoDia ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">{formatarDiaEData()}</p>
      {registros.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma aula prevista para hoje.</p>
      ) : (
        <div className="space-y-2">
          {registros.map((registro) => (
            <RegistroDoDiaCard key={registro.horarioId} registro={registro} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Log bruto e paginado, fora do grupo de tabs por período; agrupado por dia. Filtro e paginação ficam fixos (`shrink-0`), só a lista rola. */
function AbaHistoricoCompleto({ alunoId }: { alunoId: string }) {
  const [pagina, setPagina] = useState(1);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [diasRecolhidos, setDiasRecolhidos] = useState<Set<string>>(new Set());

  const { data, loading } = useApiQuery(registrosApi.obterHistoricoCompleto, {
    alunoId,
    filtros: {
      pagina,
      dataInicio: dataInicio || undefined,
      dataFim: dataFim || undefined,
    },
  });

  function filtrar(campo: 'inicio' | 'fim', valor: string) {
    if (campo === 'inicio') {
      setDataInicio(valor);
    } else {
      setDataFim(valor);
    }
    setPagina(1);
  }

  function alternarDia(chave: string) {
    setDiasRecolhidos((atual) => {
      const novo = new Set(atual);
      if (novo.has(chave)) {
        novo.delete(chave);
      } else {
        novo.add(chave);
      }
      return novo;
    });
  }

  const registros = data?.registros ?? [];
  const grupos = agruparPorDia(registros);

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-3">
      <div className="flex shrink-0 items-center gap-2">
        <Input
          type="date"
          aria-label="De"
          value={dataInicio}
          max={dataFim || undefined}
          onChange={(e) => filtrar('inicio', e.target.value)}
          className="w-auto"
        />
        <span className="text-sm text-muted-foreground">até</span>
        <Input
          type="date"
          aria-label="Até"
          value={dataFim}
          min={dataInicio || undefined}
          onChange={(e) => filtrar('fim', e.target.value)}
          className="w-auto"
        />
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
        {loading || !data ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : registros.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum registro encontrado para esse intervalo.
          </p>
        ) : (
          grupos.map((grupo) => {
            const recolhido = diasRecolhidos.has(grupo.chave);
            return (
              <div key={grupo.chave} className="space-y-2">
                <button
                  type="button"
                  onClick={() => alternarDia(grupo.chave)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    recolhido
                      ? 'border-primary/40 bg-primary/5 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted/50',
                  )}
                >
                  <span>{formatarDiaEDataDe(grupo.data)}</span>
                  <ChevronDown
                    className={cn('size-3.5 transition-transform', recolhido ? '-rotate-90' : '')}
                  />
                </button>

                {!recolhido ? (
                  <div className="space-y-2">
                    {grupo.itens.map((registro) => (
                      <RegistroDoDiaCard key={registro.id} registro={registro} />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {data && data.totalPaginas > 1 ? (
        <div className="shrink-0">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  disabled={pagina <= 1}
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                />
              </PaginationItem>
              {paginasVisiveis(pagina, data.totalPaginas).map((item, i) =>
                item === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={item}>
                    <PaginationLink isActive={item === pagina} onClick={() => setPagina(item)}>
                      {item}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext
                  disabled={pagina >= data.totalPaginas}
                  onClick={() => setPagina((p) => Math.min(data.totalPaginas, p + 1))}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      ) : null}
    </div>
  );
}

export function HistoricoDialog({
  open,
  onOpenChange,
  alunoId,
  alunoNome,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alunoId: string | null;
  alunoNome: string;
}) {
  const [periodo, setPeriodo] = useState<PeriodoHistorico>('SEMANA');
  const [visualizacao, setVisualizacao] = useState<'periodo' | 'completo'>('periodo');

  // Cache por período. Busca manual num efeito só (fetch + cache no mesmo lugar): dois hooks dependendo de timing
  // faziam as abas convergirem pro mesmo dado. `cancelado` ignora resposta que chega após trocar de aba.
  const [cache, setCache] = useState<
    Partial<Record<PeriodoHistorico, HistoricoAcompanhamentoOutputType>>
  >({});
  const [carregando, setCarregando] = useState(false);
  const { execute } = useApiLazyQuery(registrosApi.obterHistorico);

  useEffect(() => {
    setCache({});
    setVisualizacao('periodo');
  }, [alunoId]);

  useEffect(() => {
    if (!alunoId || cache[periodo]) {
      return;
    }
    let cancelado = false;
    setCarregando(true);
    execute({ alunoId, filtros: { periodo } })
      .then((resultado) => {
        if (!cancelado) {
          setCache((atual) => ({ ...atual, [periodo]: resultado }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelado) {
          setCarregando(false);
        }
      });
    return () => {
      cancelado = true;
    };
  }, [alunoId, periodo]);

  const data = cache[periodo];
  const loading = !data && carregando;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Altura travada (max-h) + overflow-hidden: cabeçalho e tabs ficam fixos (shrink-0), só a área de conteúdo rola. */}
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>{alunoNome} · Histórico de acompanhamento</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="flex shrink-0 items-center justify-between gap-2">
            <Tabs
              value={periodo}
              onValueChange={(v) => {
                setPeriodo(v as PeriodoHistorico);
                setVisualizacao('periodo');
              }}
            >
              <TabsList>
                {PERIODOS.map((p) => (
                  <TabsTrigger key={p.valor} value={p.valor}>
                    {p.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <button
              type="button"
              onClick={() => setVisualizacao((v) => (v === 'completo' ? 'periodo' : 'completo'))}
              className="shrink-0 text-sm text-primary underline-offset-4 hover:underline"
            >
              {visualizacao === 'completo' ? '← Voltar' : 'Histórico completo'}
            </button>
          </div>

          {visualizacao === 'completo' ? (
            alunoId ? (
              <AbaHistoricoCompleto alunoId={alunoId} />
            ) : null
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
              {loading || !data ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-16 rounded-lg" />
                    ))}
                  </div>
                  {periodo !== 'DIA' ? (
                    <div className="grid grid-cols-2 gap-3">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-16 rounded-lg" />
                      ))}
                    </div>
                  ) : null}
                  <Skeleton className="h-24 rounded-lg" />
                </div>
              ) : periodo === 'DIA' ? (
                <AbaDia registrosDoDia={data.registrosDoDia} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Metrica titulo="Previstas" valor={String(data.previstas)} />
                    <Metrica titulo="Registradas" valor={String(data.realizadas)} />
                    <Metrica titulo="Presença" valor={`${data.presencaPercentual}%`} />
                    <Metrica
                      titulo="Tarefas feitas"
                      valor={
                        data.tarefasFeitasPercentual === null
                          ? '—'
                          : `${data.tarefasFeitasPercentual}%`
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Metrica titulo="Foco" valor={formatarNota(data.mediaFoco)} />
                    <Metrica titulo="Autonomia" valor={formatarNota(data.mediaAutonomia)} />
                    <Metrica titulo="Comportamento" valor={formatarNota(data.mediaComportamento)} />
                    <Metrica titulo="Desempenho" valor={formatarNota(data.mediaDesempenho)} />
                  </div>

                  {periodo !== 'TUDO' ? (
                    <div className="rounded-lg border p-3">
                      <p className="mb-2 text-xs text-muted-foreground uppercase">
                        Evolução (vs. período anterior)
                      </p>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Foco</p>
                          <p className="font-medium">{formatarEvolucao(data.evolucao.foco)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Autonomia</p>
                          <p className="font-medium">{formatarEvolucao(data.evolucao.autonomia)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Comportamento</p>
                          <p className="font-medium">
                            {formatarEvolucao(data.evolucao.comportamento)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Desempenho</p>
                          <p className="font-medium">
                            {formatarEvolucao(data.evolucao.desempenho)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
